import { WebViewerInstance } from '@pdftron/webviewer'

import { getHeaderItem, zoomOut, zoomIn } from './pdftronUtils'
import {
    DISABLED_TOOLS,
    DownloadButton,
    PrintButton,
    ZoomOutButton,
    ZoomInButton,
} from './constants'
import BackTo3DModelButton from './BackTo3DModelButton'

import { PDFViewerProps } from './index'

const DISABLED_GROUPS = [
    'toolbarGroup-Edit',
    'toolbarGroup-Insert',
    'toolbarGroup-Measure',
    'toolbarGroup-Shapes',
    'toolbarGroup-View',
    'toolbarGroup-Annotate',
]

const DISABLED_ELEMENTS = [
    'signaturePanelButton',
    'opacitySlider',
    'linkButton',
    'annotationRedactButton',
    'signaturePanelButton',
    'leftPanelButton',
    'ribbons',
    'themeChangeButton',
    'menuButton',
]

function configureToolbars(
    instance: WebViewerInstance,
    props: PDFViewerProps,
    defautHeaders: any,
): void {
    // Disable all other toolbars, we will use just the annotate toolbar for now
    instance.disableElements(DISABLED_GROUPS)

    // Disable the ribbons to hide the toolbar selection buttons
    instance.disableElements([
        'ribbons',
        'fullscreenButton',
        'downloadButton',
        'printButton',
        'zoomInButton',
        'zoomOutButton',
    ])
    instance.enableFeatures([instance.Feature.NotesPanel])

    instance.setHeaderItems(header => {
        const { backTo3dModelButtonOnClick } = props
        const headerItems = defautHeaders

        const backTo3dModelButton = BackTo3DModelButton({
            onClick: backTo3dModelButtonOnClick,
        })

        const viewControlsButton = getHeaderItem(
            headerItems,
            'viewControlsButton',
        )
        const zoomOverlayButton = getHeaderItem(
            headerItems,
            'zoomOverlayButton',
        )
        const panToolButton = {
            type: 'toolButton',
            toolName: 'Pan',
            dataElement: 'panToolButton',
        }
        const searchButton = getHeaderItem(headerItems, 'searchButton')
        const toggleNotesButton = getHeaderItem(
            headerItems,
            'toggleNotesButton',
        )
        const menuButton = getHeaderItem(headerItems, 'menuButton')
        const downloadButton = {
            ...DownloadButton,
            onClick: () => {
                instance.downloadPdf({
                    filename: props.pdfName,
                    includeAnnotations: true,
                })
            },
        }

        const printButton = {
            ...PrintButton,
            onClick: () => {
                instance.print()
            },
        }
        const zoomInButton = {
            ...ZoomInButton,
            onClick: () => zoomIn(instance),
        }
        const zoomOutButton = {
            ...ZoomOutButton,
            onClick: () => zoomOut(instance),
        }

        header.update([
            backTo3dModelButton,
            {
                type: 'spacer',
            },
            viewControlsButton,
            zoomOverlayButton,
            zoomOutButton,
            zoomInButton,
            {
                type: 'divider',
            },
            panToolButton,
            {
                type: 'spacer',
            },
            searchButton,
            toggleNotesButton,
            downloadButton,
            menuButton,
            printButton,
        ])
    })
}

function configureTools(instance: WebViewerInstance): void {
    // Disable tools we do not want
    instance.disableTools(DISABLED_TOOLS)

    // Disable elements
    instance.disableElements(DISABLED_ELEMENTS)

    // Disable reply for comments
    instance.disableReplyForAnnotations(() => true)
}

export default function apply(
    instance: WebViewerInstance,
    props: PDFViewerProps,
    defautHeaders: any,
) {
    // Set default color palette for all tools
    configureToolbars(instance, props, defautHeaders)

    // Set default color palette for all tools
    configureTools(instance)

    // Put WebViewer into readonly mode:
    // Annotations cannot be created, modified, or deleted.
    // No replies can be added to any annotation messages.
    instance.annotManager.setReadOnly(true)
}
